// web/src/pages/invoices/InvoicesListPage.tsx
//
// I-1 — Invoices list page.
//
// Canonical chrome (Plan IV Task 3):
// - search: backend ?q= filter on InvoiceReferenceNo (server-side via params).
// - filterChips: single-select view (All / Open / Overdue / Paid this week /
//   Issued today) driving backend status= + from= params.
// - itemCount: wired from data?.totalCount.
//
// Drops: legacy filterBar (saved-view button row + multi-select status pills +
// 9-input date/amount/client/account grid). The single-select view chips
// subsume the saved-view + status-pill rows per Plan IV §P1.
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  listInvoices,
  type InvoiceSummaryDto,
  type ListInvoicesParams,
} from "@/lib/invoices-payments";
import { InvoiceStatusBadge, type InvoiceStatusCode } from "@/components/invoices-payments";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { useAuth } from "@/app/auth-context";
import { Plus } from "lucide-react";
import { formatDate } from "@/lib/format";

// ---------------------------------------------------------------------------
// View chips — single-select, drives backend status + from filters
// ---------------------------------------------------------------------------

type ViewChipId = "all" | "open" | "overdue" | "paidThisWeek" | "issuedToday";

const VIEW_CHIP_OPTIONS: ReadonlyArray<{ id: ViewChipId; label: string }> = [
  { id: "all",          label: "All" },
  { id: "open",         label: "Open invoices" },
  { id: "overdue",      label: "Overdue" },
  { id: "paidThisWeek", label: "Paid this week" },
  { id: "issuedToday",  label: "Issued today" },
];

interface ViewParams {
  status?: InvoiceStatusCode[];
  from?: string;
}

function applyView(view: ViewChipId): ViewParams {
  switch (view) {
    case "open":
      return { status: ["Sent", "PartPaid"] };
    case "overdue":
      return { status: ["Overdue"] };
    case "paidThisWeek":
      // Spec acknowledges Plan I server doesn't filter by paid-on; status-only.
      return { status: ["Paid"] };
    case "issuedToday": {
      const startOfDayUtc = new Date();
      startOfDayUtc.setUTCHours(0, 0, 0, 0);
      return { from: startOfDayUtc.toISOString() };
    }
    case "all":
    default:
      return {};
  }
}

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

function daysOverdue(dueDate: string | null, status: string): number | null {
  if (!dueDate) return null;
  if (status === "Paid" || status === "OverPaid" || status === "Cancelled") return null;
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return null;
  const now = new Date();
  const diffMs = now.getTime() - due.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return days > 0 ? days : null;
}

// ---------------------------------------------------------------------------
// InvoicesListPage
// ---------------------------------------------------------------------------

export function InvoicesListPage() {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const [searchText, setSearchText] = useState("");
  const [selectedView, setSelectedView] = useState<ViewChipId>("all");
  const [page, setPage] = useState(0);

  const params = useMemo<ListInvoicesParams>(() => {
    const view = applyView(selectedView);
    return {
      status: view.status,
      from: view.from,
      q: searchText.trim() || undefined,
      page,
      pageSize: 50,
    };
  }, [selectedView, searchText, page]);

  const { data, isLoading } = useQuery({
    queryKey: ["invoices", params],
    queryFn: () => listInvoices(params),
  });

  // Sort state — client-side only; listInvoices has no `sort` param.
  // Sort applies to the current page's rows, not the full result set.
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const visibleRows = useMemo(() => {
    const rows = data?.items ?? [];
    if (!sort) return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const valueFor = (r: InvoiceSummaryDto): string | number | null => {
      switch (sort.columnId) {
        case "ref":         return r.invoiceReferenceNo;
        case "issued":      return r.invoiceDate ?? null;
        case "due":         return r.dueDate ?? null;
        case "client":      return r.clientFullName ?? null;
        case "account":     return r.accountReferenceNo ?? null;
        case "total":       return r.invoiceTotalIncGst;
        case "balance":     return r.balanceOutstanding;
        case "status":      return r.invoiceStatusCode;
        case "daysOverdue": return daysOverdue(r.dueDate, r.invoiceStatusCode);
        case "myob":        return r.myobExported ? 1 : 0;
        default:            return null;
      }
    };
    return [...rows].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [data, sort]);

  // TODO(Plan II/III): Bulk actions deferred — CSV export and "Issue selected"
  // require a checkbox-column DataGrid extension and a multi-issue endpoint.
  // Spec lines 4011–4017 list both as Plan I items but they're moved out of
  // scope for this task per the executor brief.

  const cols: DataGridColumn<InvoiceSummaryDto>[] = [
    {
      id: "ref",
      header: "Invoice #",
      sortable: true,
      width: "100px",
      accessor: (r) => (
        <Link to={`/invoices/${r.invoiceId}`} className="underline text-primary">
          {r.invoiceReferenceNo}
        </Link>
      ),
    },
    {
      id: "issued",
      header: "Issued",
      sortable: true,
      width: "100px",
      accessor: (r) => r.invoiceDate ? formatDate(r.invoiceDate) : "—",
    },
    {
      id: "due",
      header: "Due",
      sortable: true,
      width: "100px",
      accessor: (r) => r.dueDate ? formatDate(r.dueDate) : "—",
    },
    // Adapt: backend returns null for ClientFullName / AccountReferenceNo on
    // the Summary DTO (Task 21 left these unpopulated). Show em-dash.
    {
      id: "client",
      header: "Client",
      sortable: true,
      accessor: (r) =>
        r.clientId ? (
          <Link to={`/clients/${r.clientId}`} className="underline text-primary hover:text-primary/80 transition-colors">
            {r.clientFullName ?? "—"}
          </Link>
        ) : (
          r.clientFullName ?? "—"
        ),
    },
    { id: "account", header: "Account", sortable: true, accessor: (r) => r.accountReferenceNo ?? "—" },
    {
      id: "total",
      header: "Total",
      sortable: true,
      width: "100px",
      accessor: (r) => fmt.format(r.invoiceTotalIncGst),
      align: "right",
    },
    {
      id: "balance",
      header: "Balance",
      sortable: true,
      width: "100px",
      accessor: (r) => fmt.format(r.balanceOutstanding),
      align: "right",
    },
    {
      id: "daysOverdue",
      header: "Days overdue",
      sortable: true,
      width: "110px",
      accessor: (r) => {
        const d = daysOverdue(r.dueDate, r.invoiceStatusCode);
        return d == null ? "—" : String(d);
      },
      align: "right",
    },
    {
      id: "myob",
      header: "MYOB",
      sortable: true,
      width: "70px",
      accessor: (r) => (r.myobExported ? "✓" : "—"),
      align: "center",
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      width: "90px",
      accessor: (r) => <InvoiceStatusBadge code={r.invoiceStatusCode as InvoiceStatusCode} />,
    },
  ];

  return (
    <ListPage
      title="Invoices"
      primaryAction={
        permissions.has("invoices:write") ? (
          <IntentButton onClick={() => navigate("/invoices/new")}>
            <Plus className="mr-1.5 h-4 w-4" /> Add Invoice
          </IntentButton>
        ) : undefined
      }
      search={{
        value: searchText,
        onChange: (v) => { setSearchText(v); setPage(0); },
        placeholder: "Search invoice number…",
      }}
      filterChips={{
        options: VIEW_CHIP_OPTIONS,
        value: selectedView,
        onChange: (next) => { setSelectedView(next); setPage(0); },
        "aria-label": "Invoice view filter",
      }}
    >
      <DataGrid
        rows={visibleRows}
        columns={cols}
        rowKey={(r) => r.invoiceId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        emptyHeadline="No invoices match these filters."
        onPageChange={setPage}
        onSortChange={setSort}
        onRowClick={(r) => navigate(`/invoices/${r.invoiceId}`)}
      />
    </ListPage>
  );
}
